const ENCODER = new TextEncoder();

export const CONDITION_WRITE_MAX_BYTES = 1024 * 1024;
export const CONDITION_WIRE_TARGET_BYTES = 768 * 1024;
export const CONDITION_ROW_WARNING_BYTES = 64 * 1024;
// Lean format-3 carriers left two genuinely dense multi-sport actuals at
// 131-133 KiB in the deliberately conservative jsonb::text diagnostic.
// Keep the 64 KiB warning, but use the evidence-approved 192 KiB contract.
export const CONDITION_ROW_CONTRACT_BYTES = 192 * 1024;

export type WriteBudgetChunk<T> = {
  rows: T[];
  bytes: number;
  oversizedRows: number;
};

/**
 * The UTF-8 length of a string without encoding a copy of it: Buffer.byteLength where Node's Buffer
 * exists (every server route), TextEncoder otherwise. The two agree on every string, a lone
 * surrogate counting as the three bytes of U+FFFD in both, and JSON.stringify never emits one. The
 * encoded copy was a second buffer as large as the text (lane fix/p3g-conditions-memory: 56 to
 * 68 MB of external memory for a surf slice's observation rows).
 */
export const utf8ByteLength: (text: string) => number = typeof Buffer === "function"
  ? (text) => Buffer.byteLength(text, "utf8")
  : (text) => ENCODER.encode(text).byteLength;

export function serialisedJsonBytes(value: unknown): number {
  return utf8ByteLength(JSON.stringify(value) ?? "null");
}

/**
 * serialisedJsonBytes of an array, from the serialised sizes of its elements: the two brackets and
 * one comma between elements, so the array's own text is never built. Exact for rows and any
 * value without a key-dependent toJSON (an element JSON.stringify leaves out is "null" in an
 * array, as serialisedJsonBytes counts it).
 */
export function serialisedJsonArrayBytes(elementBytes: readonly number[]): number {
  let bytes = 2;
  for (let index = 0; index < elementBytes.length; index++) bytes += (index > 0 ? 1 : 0) + elementBytes[index];
  return bytes;
}

export function assertConditionStatementWithinBudget(
  value: unknown,
  label: string,
): number {
  const bytes = serialisedJsonBytes(value);
  if (bytes > CONDITION_WRITE_MAX_BYTES) {
    throw new Error(
      `${label} serialised payload ${bytes} bytes exceeds the ${CONDITION_WRITE_MAX_BYTES} byte conditions statement limit`,
    );
  }
  return bytes;
}

export function chunkRowsByBudget<T>(
  rows: readonly T[],
  options: { maxRows: number; maxBytes: number },
): WriteBudgetChunk<T>[] {
  const maxRows = Math.max(1, Math.floor(options.maxRows));
  const maxBytes = Math.max(2, Math.floor(options.maxBytes));
  const chunks: WriteBudgetChunk<T>[] = [];
  let currentRows: T[] = [];
  let currentBytes = 2;

  const flush = (): void => {
    if (currentRows.length === 0) return;
    chunks.push({ rows: currentRows, bytes: currentBytes, oversizedRows: 0 });
    currentRows = [];
    currentBytes = 2;
  };

  for (const row of rows) {
    const rowBytes = serialisedJsonBytes(row);
    const singleStatementBytes = rowBytes + 2;
    if (singleStatementBytes > maxBytes) {
      flush();
      chunks.push({
        rows: [row],
        bytes: singleStatementBytes,
        oversizedRows: 1,
      });
      continue;
    }

    const separatorBytes = currentRows.length > 0 ? 1 : 0;
    if (
      currentRows.length >= maxRows ||
      currentBytes + separatorBytes + rowBytes > maxBytes
    ) {
      flush();
    }

    currentRows.push(row);
    currentBytes += (currentRows.length > 1 ? 1 : 0) + rowBytes;
  }

  flush();
  return chunks;
}

export function chunkConditionRpcRows<T>(
  rows: readonly T[],
  options: { maxRows: number },
): WriteBudgetChunk<T>[] {
  const emptyRowsBytes = serialisedJsonBytes([]);
  const wrapperOverheadBytes = serialisedJsonBytes({ rows: [] }) - emptyRowsBytes;
  const chunks = chunkRowsByBudget(rows, {
    maxRows: options.maxRows,
    maxBytes: CONDITION_WRITE_MAX_BYTES - wrapperOverheadBytes,
  });

  return chunks.map((chunk) => {
    const bytes = assertConditionStatementWithinBudget(
      { rows: chunk.rows },
      "rescore RPC",
    );
    return { ...chunk, bytes };
  });
}
